import type { ReactElement, ReactNode } from 'react';
import EmberFlame from '../../../../../shared-web/EmberFlame';
import { letterDateLine, ordinalLine, parseLetter, readsLine, type LetterRun } from '../letter-format';
import '../pages/LetterPage.css';

/**
 * The weekly letter as ONE SHEET (§4.20, 2026-09-23 二次拍板) — the paper its
 * two pages share: the person's own at /me/letters/:id (LetterPage) and the
 * one someone handed them at /l/:token (LetterHandoffPage, 2026-09-24). Its
 * visual structure comes from the letter's own parts (letter-format.ts reads
 * brain's marks): the letterhead — the flame, "A letter from Ember", the day,
 * and the facts the page supplies; the words — the person's own marked the
 * way key sentences are marked on an Ember page, the day they said them
 * beside; the movement, when there is one; the question against its amber
 * rule; Ember's name; then whatever rows the page puts at the foot.
 */
export function LetterSheet({ letter, facts = null, strip = null, children, logSeen, logSeenAttrs = {} }: {
  letter: { id: string; written_at: string | null; letter: string; ordinal?: number | null; reads_from?: string | null };
  /** The letterhead's facts under the day: 'own' = which letter and what days it reads; a node = the page's own line; null = none. */
  facts?: 'own' | ReactNode | null;
  /** Under the facts: their path, small, on their own letter (2026-11-03); never on a letter handed on. */
  strip?: ReactNode;
  /** The sheet's last rows — the doors. */
  children?: ReactNode;
  logSeen: string;
  logSeenAttrs?: Record<string, string | undefined>;
}): ReactElement {
  const blocks = parseLetter(letter.letter);
  const reads = facts === 'own' ? readsLine(letter.reads_from, letter.written_at) : '';
  return (
    <article className="weekly-letter-sheet" aria-label="A letter from Ember">
      {/* Seen on its head: a long letter is never half in view (2026-11-05). */}
      <header className="weekly-letter-head" data-log-seen={logSeen} {...logSeenAttrs}>
        <div className="weekly-letter-head-top">
          <span className="weekly-letter-head-from">
            <span className="weekly-letter-head-mark" aria-hidden><EmberFlame className="weekly-letter-head-flame" /></span>
            A letter from Ember
          </span>
          <p className="weekly-letter-head-date">{letterDateLine(letter.written_at)}</p>
        </div>
        {facts === 'own' ? (
          <dl className="weekly-letter-facts">
            <div className="weekly-letter-fact"><dt>Which letter</dt><dd>{ordinalLine(letter.ordinal)}</dd></div>
            {reads && <div className="weekly-letter-fact"><dt>What it reads</dt><dd>{reads}</dd></div>}
          </dl>
        ) : facts}
        {strip}
      </header>

      <div className="weekly-letter-body">
        {blocks.map((b, i) => {
          if (b.kind === 'move') {
            return (
              <figure className="weekly-letter-move" key={i} aria-label="What moved">
                <figcaption className="weekly-letter-move-label">What moved</figcaption>
                <div className="weekly-letter-move-ends">
                  <div className="weekly-letter-move-end">
                    {b.from.day && <span className="weekly-letter-day">{b.from.day}</span>}
                    <p dir="auto"><mark className="weekly-letter-words">{b.from.text}</mark></p>
                  </div>
                  <span className="weekly-letter-move-arrow" aria-hidden><svg viewBox="0 0 24 24"><path d="M5 12h14M13 6l6 6-6 6" /></svg></span>
                  <div className="weekly-letter-move-end">
                    {b.to.day && <span className="weekly-letter-day">{b.to.day}</span>}
                    <p dir="auto"><mark className="weekly-letter-words">{b.to.text}</mark></p>
                  </div>
                </div>
              </figure>
            );
          }
          if (b.kind === 'question') {
            return (
              <aside className="weekly-letter-question" key={i}>
                <p dir="auto">{b.text}</p>
                <small>{facts === 'own' ? 'One question. Yours to leave alone.' : 'One question. Theirs to leave alone.'}</small>
              </aside>
            );
          }
          return <p className="weekly-letter-para" key={i} dir="auto">{b.runs.map(renderRun)}</p>;
        })}
      </div>

      <p className="weekly-letter-sign" aria-hidden>— Ember</p>
      {children}
    </article>
  );
}

/** A paragraph's run: prose as it is; the person's own words marked, their day beside. */
function renderRun(run: LetterRun, i: number): ReactElement {
  if (run.kind === 'text') return <span key={i}>{run.text}</span>;
  return (
    <span key={i} className="weekly-letter-words-wrap">
      <mark className="weekly-letter-words">“{run.text}”</mark>
      {run.day && <span className="weekly-letter-day">{run.day}</span>}
    </span>
  );
}

/** The sheet's door row — "Have thoughts about this?" and the recipient's "Talk with Ember" wear it. */
export function LetterDoor({ title, sub, log, logAttrs = {}, onClick }: {
  title: string;
  sub?: string;
  log: string;
  logAttrs?: Record<string, string | undefined>;
  onClick: () => void;
}): ReactElement {
  return (
    <button type="button" className="weekly-letter-reply" data-log={log} {...logAttrs} onClick={onClick}>
      <svg className="weekly-letter-reply-glyph" viewBox="0 0 24 24" aria-hidden>
        <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v7a2.5 2.5 0 0 1-2.5 2.5H10l-4.5 4v-4A2.5 2.5 0 0 1 4 13.5z" />
      </svg>
      <span>
        {title}
        {sub && <span className="weekly-letter-reply-sub">{sub}</span>}
      </span>
      <svg className="weekly-letter-reply-chevron" viewBox="0 0 24 24" aria-hidden><path d="M9 6l6 6-6 6" /></svg>
    </button>
  );
}
